import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PropTypes from 'prop-types';
import SideNavbar from "../components/SideNavbar";
import Error from "./Error";
import Loading from "./Loading";
import HoverButton from "../components/HoverButton";
import { AiOutlineClose } from "react-icons/ai";
import { BsPlusSquare, BsPlusSquareFill } from "react-icons/bs";
import CropImage from "../components/CropImage";
import BuckList from "../components/BuckList";
import FormInput from "../components/FormInput";
import { createDeepClone, getObjectValue } from "../utils/utils";
import { FaArrowLeftLong } from "react-icons/fa6";
import { FaLongArrowAltRight } from "react-icons/fa";
import { v4 as uuidv4 } from 'uuid';
import BuckForm from "../components/BuckForm";
import Modal from "../components/Modal";
import BuckProfile from "../components/BuckProfile";
import Pagination from "../components/Pagination";
import { log } from "../utils/logUtils";

const defaultFormDataErrors = {
    "nameError": '',
    "primaryProfileImageError": '',
    "topSixProfileImageError": '',
    "multipleBucksInImageError": '',
    "allDeerLabeledError": '',
    "ageError": '',
    "pointsError": '',
    "classificationError": '',
    "notesErrorError": '',
    "privateNotesError": '',
    "publishedError": '',
};

const defaultFormData = {
    "name": '',
    "primaryProfileImage": false,
    "topSixProfileImage": false,
    "multipleBucksInImage": false,
    "allDeerLabeled": false,
    "age": '',
    "points": '',
    "classification": '',
    "isBuck": false,
    "notes": '',
    "privateNotes": '',
    "published": false,
};


function LabelImages({ currentProfile, setCurrentProfile, isMobile }) {
    const [selectedSite, setSelectedSite] = useState(null);
    const [currentChunk, setCurrentChunk] = useState(0);
    const [totalChunks, setTotalChunks] = useState(null);
    const [chunkSize, setChunkSize] = useState(0);
    const [imagesToBeLabeled, setImagesToBeLabeled] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageBeingMarked, setImageBeingMarked] = useState(null);
    const [createNewBuck, setCreateNewBuck] = useState(false);
    const [bucksObject, setBucksObject] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [formData, setFormData] = useState(createDeepClone(defaultFormData));
    const [formDataErrors, setFormDataErrors] = useState(createDeepClone(defaultFormDataErrors));
    const [submissionError, setSubmissionError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [changesMade, setChangesMade] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [buckInventoryFetched, setBuckInventoryFetched] = useState(false);
    const [unlabeledImagesFetched, setUnlabeledImagesFetched] = useState(false);
    const [siteFetched, setSiteFetched] = useState(false);
    const [showModal, setShowModal] = useState(null);

    const { site } = useParams();

    let formattedSite = "site";
    if(site) {
        formattedSite = site.replaceAll("-wildlifeinvestments", "");
        let sites = formattedSite.split("-");
        formattedSite = sites.map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join(' ');
    }

    useEffect(() => {
        document.title = `Label ${formattedSite} Images`;
    });

    useEffect(() => {
        if(currentProfile && currentProfile.sites) {
            currentProfile.checkSitesForSelectedSite(site, setSelectedSite).then((wasFound) => {
                setSiteFetched(wasFound);
            })
        } else if (currentProfile) {
            setSiteFetched(true);
        }
    }, [currentProfile, site]);

    useEffect(() => {
        if(selectedSite && currentProfile && typeof currentChunk === "number") {
            selectedSite.getSiteUnlabeledImages(currentProfile.accessToken, currentChunk).then((response) => {
                // console.log(response);
                if(response && getObjectValue(response, "images")) {
                    setChunkSize(response.chunkSize);
                    setTotalChunks(response.totalChunks);
                    setImagesToBeLabeled(response.images);
                } else if(!response) {
                    setFetchError("Something went wrong fetching unlabeled images, please refresh the page and try again.");
                }
                setUnlabeledImagesFetched(true);
            });
            selectedSite.getBuckInventory(currentProfile.accessToken).then((response) => {
                if(response && Object.keys(response).length > 0) {
                    setBucksObject(response);
                }
                if(!response) {
                    setFetchError("Something went wrong fetching the buck inventory, please refresh the page and try again.");
                }
                setBuckInventoryFetched(true);
            });
        }
    }, [selectedSite, currentProfile, currentChunk]);

    const handleChange = (e) => { 
        // console.log(e);
        let updatedFormData = { ...formData, ...e};

        if(getObjectValue(updatedFormData, "name") && getObjectValue(updatedFormData, "age") && getObjectValue(updatedFormData, "points") && getObjectValue(updatedFormData, "classification")) {
            setChangesMade(true);
        }
        setFormData(updatedFormData);
    };

    const submitLabel = async (image, imageName, originalImage) => {
        let response = await selectedSite.saveLabel(currentProfile.accessToken, formData, image, imageName, originalImage);
        if(response) {
            if(formData.multipleBucksInImage && !formData.allDeerLabeled) {
                log("No need to clear the image from images to be labeled", "Label Submission Result", false, false);
            } else {
                let newImagesToBeLabeled = [...imagesToBeLabeled];
                newImagesToBeLabeled.splice(selectedImage, 1);
                setImagesToBeLabeled(newImagesToBeLabeled);
            }
            clearMarkingData(true);
            setFormDataErrors(createDeepClone(defaultFormDataErrors));
        } else {
            setSubmissionError('Failed to save and upload label, please refresh the page and try again.')
        }

        return response;
    };

    const clearMarkingData = (doneWithCurrentImage) => {
        if(doneWithCurrentImage) {
            setSelectedImage(null);
            setImageBeingMarked(null);
        }
        setCreateNewBuck(false); 
        setSubmissionError(null);
        setFormData(createDeepClone(defaultFormData));
        setSubmitted(false);
    };

    const onPageChanged = (data) => {
        let zeroIndexCurrentPage = data.currentPage - 1;
        if(zeroIndexCurrentPage !== currentChunk) {
            setFetchError('');
            setCurrentChunk(zeroIndexCurrentPage);
        }
    };

    if(selectedSite && currentProfile && currentProfile.isAdmin && buckInventoryFetched && unlabeledImagesFetched) {
        let sitesURI = "/sites";
        let adminLinks = [];
        if(currentProfile.isAdmin) {
            // sitesURI = `/${sub}/sites`;
            adminLinks = [{ "display": "Label Images", "url": `${sitesURI}/${site}/label`}];
        }
        let sideNavbarFilters = [{ "display": "Buck Inventory", "url": `${sitesURI}/${site}/inventory` },
                                 { "display": "Image Upload", "url": `${sitesURI}/${site}/upload` }, ...adminLinks];

        return (
            <>
                <SideNavbar isMobile={isMobile} propFilters={sideNavbarFilters} title={formattedSite} titleUrl={sitesURI} initialShow={true} />
                <div style={isMobile ? { "minHeight":"92vh" } : { "minHeight":"92vh", "maxWidth": "75%", "display": "flex", "justifyContent": "center" }} className="w3-content w3-container w3-padding-64" id="about">
                    {imageBeingMarked ? (
                        <div>
                            <div style={isMobile ? {} : { "width": "100%", "display": "flex", "justifyContent": "center" }}>
                                <div>
                                    <HoverButton
                                        normalIcon={<FaArrowLeftLong style={{ "transform": "scale(2.5)", "color": "grey" }} className="ai-edit-icon" />}
                                        hoverIcon={<FaArrowLeftLong style={{ "transform": "scale(2.5)", "color": "black" }} className="ai-edit-icon" />}
                                        updateParent={() => { clearMarkingData(true); }}
                                        title={"Back to Images"}
                                        style={isMobile ? { "alignItems": "top", "marginLeft": "3px", "marginRight": "32px", "marginTop": "16px", "marginBottom": "16px", "width": "100%", "justifyContent": "left" } : { "alignItems": "top", "marginLeft": "3px", "marginRight": "32px", "marginTop": "8px" }}
                                    />
                                </div>
                                <BuckList style={{ "marginRight": "48px" }} isMobile={true} bucksObject={bucksObject} onDoubleClick={(e) => setShowModal(e)} />
                                {showModal ? (
                                    <Modal isMobile={isMobile} closeButton={true} onClose={() => setShowModal(null)}>
                                        <BuckProfile buck={bucksObject[showModal]} site={site} />
                                    </Modal>
                                ) : null}
                                <div>
                                    <CropImage style={{ "maxWidth": "50vh" }} isMobile={isMobile} imageUrl={imageBeingMarked} alt="Unlabeled Animal" setCroppedImage={setCroppedImage} />
                                    {createNewBuck ? (
                                        <div style={{ "width": "100%", "display": "flex" }}>
                                            <BuckForm
                                                style={isMobile ? { "marginTop": "16px", "width": "100%" } : { "marginTop": "16px" }}
                                                isMobile={isMobile} 
                                                onSubmit={() => { 
                                                    setSubmitted(true); 
                                                    setSubmissionError(null); 
                                                    setFormData({ ...formData, "isBuck": true }); 
                                                    let image = imagesToBeLabeled[selectedImage].fileName;
                                                    let originalImage = null;
                                                    let imageName = null;
                                                    if(croppedImage) {
                                                        originalImage = image;
                                                        let imageType = image.split(".")[1];
                                                        imageName = `${image.split(".")[0]}-${uuidv4()}.${imageType}`;
                                                        image = croppedImage;
                                                    }
                                                    submitLabel(image, imageName, originalImage); 
                                                }} 
                                                onCancel={clearMarkingData} 
                                                handleChange={handleChange} 
                                                formData={formData} 
                                                formDataErrors={formDataErrors} 
                                                newBuck={createNewBuck}
                                                submitted={submitted}
                                                changesMade={changesMade}
                                            />
                                        </div>
                                    ) : (
                                        <div style={{ "display": "flex", "marginTop": "16px" }}>
                                            <div style={{ "width": "75%" }}>
                                                {bucksObject ? (
                                                    <form
                                                        onSubmit={async (e) => { 
                                                            e.preventDefault();
                                                            setSubmissionError(null);
                                                            setFormData({ ...formData, "isBuck": true });
                                                            let image = imagesToBeLabeled[selectedImage].fileName;
                                                            let originalImage = null;
                                                            let imageName = null;
                                                            if(croppedImage) {
                                                                originalImage = image;
                                                                let imageType = image.split(".")[1];
                                                                imageName = `${image.split(".")[0]}-${uuidv4()}.${imageType}`;
                                                                image = croppedImage;
                                                            }
                                                            submitLabel(image, imageName, originalImage);
                                                        }}
                                                    >
                                                        <div className="form-group">
                                                            <label htmlFor="buck">Buck Pictured</label>
                                                            <FormInput isMobile={isMobile} dropDownOptions={Object.keys(bucksObject)} updateParent={handleChange} placeholder="Select Buck" name="name" type="dropdown" object={formData} reaction={{ "border": "1px solid #ccc" }} />
                                                        </div>
                                                        <div style={{ "display": "flex", "marginBottom": "15px" }}>
                                                            <label style={{ "width": "100%", "margin": "0px", "display": "block", "fontWeight": "bold" }}>Multiple Bucks In Image</label>
                                                            <FormInput disabled={submitted} isMobile={isMobile} updateParent={handleChange} name="multipleBucksInImage" type="checkbox" object={formData} errors={formDataErrors} />
                                                        </div>
                                                        <div style={{ "display": "flex", "marginBottom": "15px" }}>
                                                            <label style={{ "width": "100%", "margin": "0px", "display": "block", "fontWeight": "bold" }}>Set as Primary Profile Image</label>
                                                            <FormInput isMobile={isMobile} updateParent={handleChange} name="primaryProfileImage" type="checkbox" object={formData} errors={formDataErrors} />
                                                        </div>
                                                        <div style={{ "display": "flex", "marginBottom": "15px" }}>
                                                            <label style={{ "width": "100%", "margin": "0px", "display": "block", "fontWeight": "bold" }}>Set as Top 6 Image in Profile</label>
                                                            <FormInput isMobile={isMobile} updateParent={handleChange} name="topSixProfileImage" type="checkbox" object={formData} errors={formDataErrors} />
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="notes">Public Notes (Visible to Everyone)</label>
                                                            <FormInput isMobile={isMobile} updateParent={handleChange} placeholder="Enter Public Notes" name="notes" type="textArea" object={formData} errors={formDataErrors} reaction={{ "border": "1px solid #ccc", "minWidth": "295px", "maxWidth": "800px" }} />
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="privateNotes">Private Notes</label>
                                                            <FormInput isMobile={isMobile} updateParent={handleChange} placeholder="Enter Private Notes" name="privateNotes" type="textArea" object={formData} errors={formDataErrors} reaction={{ "border": "1px solid #ccc", "minWidth": "295px", "maxWidth": "800px" }} />
                                                        </div>
                                                        {formData && formData.hasOwnProperty("multipleBucksInImage") && formData.multipleBucksInImage ? (
                                                            <div style={{ "display": "flex", "marginBottom": "15px" }}>
                                                                <label style={{ "width": "100%", "margin": "0px", "display": "block", "fontWeight": "bold" }}>All Unique Bucks Labeled</label>
                                                                <FormInput disabled={submitted} isMobile={isMobile} updateParent={handleChange} name="allDeerLabeled" type="checkbox" object={formData} errors={formDataErrors} />
                                                            </div>
                                                        ) : null}
                                                        <div>
                                                            <button className="main-button" type="submit" disabled={!formData.name}>Pair Image with Buck</button>
                                                        </div>
                                                    </form>
                                                ) : (
                                                    <div style={{ "display": "flex", "alignItems": "center" }}>
                                                        <p style={{ "margin": "0px" }}>No bucks found, please create a buck by pressing the button </p>
                                                            <FaLongArrowAltRight style={{ "transform": "scale(2.5)", "marginLeft": "16px", "marginRight": "16px" }} />
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ "width": "25%", "display": "flex", "justifyContent": "right", "alignItems": "flex-start" }}>
                                                <HoverButton 
                                                    normalIcon={<BsPlusSquare style={{ "transform": "scale(2.5)"}} />}
                                                    hoverIcon={<BsPlusSquareFill style={{ "transform": "scale(2.5)"}} />}
                                                    updateParent={() => { setCreateNewBuck(true); handleChange({ "isBuck": true }); }}
                                                    style={{ "marginTop": "8px" }}
                                                    title={"Create New Buck"}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {submissionError ? (
                                <div style={{ "color": "red", "margin": "16px"}}>{submissionError}</div>
                            ) : null}
                        </div>
                    ) : (
                        <div>
                            <div style={{ "display": "flex", "alignItems": "center"}}>
                                <h3 style={{ "width": "100%", "margin": "0px" }}>Label Bucks</h3>
                            </div>
                            {totalChunks && chunkSize ? (
                                <div>
                                    <Pagination totalRecords={Array.from({ length: chunkSize * totalChunks }, (_, index) => index)} pageLimit={chunkSize} pageNeighbours={1} onPageChanged={onPageChanged} />
                                </div>
                            ) : null}
                            {submissionError ? (
                                <div style={{ "color": "red", "margin": "16px"}}>{submissionError}</div>
                            ) : null}
                            {fetchError ? (
                                <div style={{ "color": "red", "margin": "16px"}}>{fetchError}</div>
                            ) : imagesToBeLabeled ? (
                                <div style={isMobile ? { "display": "flex", "flexWrap": "wrap", "width": "100%", "justifyContent": "center" } : { "display": "flex", "flexWrap": "wrap", "width": "100%" }} className="w3-col l3 s6">
                                    {imagesToBeLabeled.map((image, index) => {
                                        let url = image.presignedURL;
                                        let divStyle = { "display": "flex" };
                                        if(selectedImage === index) {
                                            divStyle = { "border": "2px solid black" };
                                        }
                                        return(
                                            <div
                                                onClick={() => {
                                                    if(selectedImage === index) {
                                                        setSelectedImage(null);
                                                    } else {
                                                        setSelectedImage(index);
                                                    }
                                                }}
                                                onDoubleClick={() => {
                                                    setSelectedImage(index);
                                                    setImageBeingMarked(image.presignedURL);
                                                }}
                                                className="dropzone-image"
                                                style={divStyle}
                                                key={index}
                                            >
                                                <img style={isMobile ? { "width": "65vw" } : { "width": "150px" }} src={url} alt={`${image.fileName}`} />
                                                <div>
                                                    <HoverButton
                                                        normalIcon={<AiOutlineClose style={{ "transform": "scale(1.5)", "color": "grey" }} className="ai-edit-icon" />}
                                                        hoverIcon={<AiOutlineClose style={{ "transform": "scale(1.5)", "color": "black" }} className="ai-edit-icon" />}
                                                        updateParent={() => { submitLabel(image.fileName); }}
                                                        style={isMobile ? { "alignItems": "top", "marginLeft": "3px", "marginRight": "4px", "width": "100%", "justifyContent": "right" } : { "alignItems": "top", "marginLeft": "3px", "marginRight": "4px" }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p>No new images to be labeled at this time.</p>
                            )}
                        </div>
                    )}
                </div>
                {typeof selectedImage === "number" && !imageBeingMarked ? (
                    <div className="navbar-bottom">
                        <div className="w3-bar w3-wide w3-padding w3-card w3-white" style={{ "display": "flex", "justifyContent": "center" }}>
                            <div style={isMobile ? { "width": "100%", "display": "flex", "justifyContent": "right" } : { "width": "75%", "display": "flex", "justifyContent": "right" }}>
                                <button style={{ "width": "150px" }} className="main-button" onClick={() => { setImageBeingMarked(imagesToBeLabeled[selectedImage].presignedURL); }} disabled={selectedImage === null}>
                                    Select Image
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </>
        );
    } else if((!currentProfile && typeof currentProfile === "boolean") || (currentProfile && !currentProfile.isAdmin)) {
        return(
            <Error isMobile={isMobile} type={"login"} />
        );
    } else if (!selectedSite && siteFetched) {
        return(
            <Error isMobile={isMobile} text={"failed to find that site"} />
        );
    } else {
        return(
            <Loading isMobile={isMobile} text={`${formattedSite}`} />
        );
    }
}

LabelImages.propTypes = {
    dataView: PropTypes.bool,
    isMobile: PropTypes.bool,
    currentProfile: PropTypes.object
};

export default LabelImages;